import { Box, Container, Typography } from '@mui/material'
import React, { useContext, useEffect, useMemo, useState } from 'react'

import { MeasureKind, UnitsByMeasure } from 'convert'
import { useIntl } from 'react-intl'
import { EUnitType } from '../../@types/Enums'
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider'
import { SceneDataContext } from '../../provider/SceneDataProvider'
import { useUnitContext } from '../../provider/UnitProvider'
import {
    CM_TO_M, DEFAULT_ZOOM_VALUE, IN_TO_M, SCALE_DEFAULT_MEASUREMENT, SCALE_INTERVAL, SCALE_LINE_TO_PIXEL, SCALE_LINE_TO_TEN
} from '../../utils/constants'
import { convertTo, getLocalizedUnitLabel } from '../../utils/conversionUtils'

const roundToInterval = (value: number, interval: number): number => Math.round(value / interval) * interval

const lineThickness = "2px";
const lineColor = "#000000";

const stylesSX = {
    container: {
        position: "absolute",
        bottom: 0,
        zIndex: 999,
        right: 0,
        marginRight: ".6rem",
        width: "fit-content",
        height: "2rem",
        display: 'flex',
        flexDirection: "row",
        "&.MuiContainer-root": { // Remove padding from the container
            padding: 0
        }
    },
    textContainer: {
        backgroundColor: '#cccccc90',
        width: "5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&.MuiContainer-root": { // Remove padding from the container
            padding: 0
        }
    },
    rulerContainer: {
        backgroundColor: '#aaaaaa20',
        width: "fit-content",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&.MuiContainer-root": { // Overwrite padding of the container
            padding: "0 1rem"
        }
    },
    textTypo: {
    },
    horizontalLine: {
        height: lineThickness,
        backgroundColor: lineColor,
    },
    lineEndings: { // Properties for both ending lines
        width: lineThickness,
        height: "6px",
        backgroundColor: lineColor,
    },
    lineEndingLeft: {
        transform: "translate(" + lineThickness + ", 3px)",

    },
    lineEndingRight: {
        transform: "translate(-" + lineThickness + ", 3px)",

    }
}

const ScaleWidget = () => {
    const { showGeneralInfo } = useMultiSelectToggleContext();

    const { zoomData } = useContext(SceneDataContext); // Get the current zoom level of the camera from the context provider

    const [measurementText, setMeasurementText] = useState<string>("0cm"); // The text to display next to the line
    const [lineSizePx, setLineSizePx] = useState<number>(1); // The length of the line in meters (1 = 100cm)
    const { unitType } = useUnitContext(); // Indicates if we need to display imperial or metric units.
    const intl = useIntl();
    // States for the localized unit type labels.
    const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "centimeter"), [intl]);
    const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "inch"), [intl]);

    // Adjust scale widget if zoomData changes
    useEffect(() => {
        // Console.log(zoomData + " (zoomdata)") // debug

        const zoomFactor = zoomData / DEFAULT_ZOOM_VALUE; // Get zoom level in percent to default (1.0 = default, example: 0.5 = zoomed out)
        const measurementCm = SCALE_DEFAULT_MEASUREMENT / zoomFactor; // Convert zoom level to the measurement in cm.
        const measurementIn = convertTo(measurementCm, "cm" as UnitsByMeasure<MeasureKind.Length>, "inch" as UnitsByMeasure<MeasureKind.Length>); // Measurement equivalent in inches.
        const roundedCm = roundToInterval(measurementCm, SCALE_INTERVAL); // Round to intervals of 5.
        const roundedIn = roundToInterval(measurementIn, SCALE_INTERVAL); // Round to intervals of 5.

        if(unitType === EUnitType.METRIC) {
            setMeasurementText(roundedCm + cmLocalizedLabel); // Set the text to the rounded metric measurement.
            /*
                (zoomData / DEFAULT_ZOOM_VALUE) equals a 100cm long line at any zoom level
                (roundedCm * 0.01) scales the 100cm long line to the displayed rounded measurement in cm.
            */
            setLineSizePx((zoomData / DEFAULT_ZOOM_VALUE) * (roundedCm * CM_TO_M) * SCALE_LINE_TO_TEN * SCALE_LINE_TO_PIXEL);
        } else {
            setMeasurementText(roundedIn + inLocalizedLabel); // Set the text to the rounded imperial measurement.
            /*
                (zoomData / DEFAULT_ZOOM_VALUE) equals a 100cm long line at any zoom level
                (roundedIn * 0.0254) scales the 100cm long line to the displayed rounded measurement in in.
            */
            setLineSizePx((zoomData / DEFAULT_ZOOM_VALUE) * (roundedIn * IN_TO_M) * SCALE_LINE_TO_TEN * SCALE_LINE_TO_PIXEL);
        }

    }, [unitType, zoomData, cmLocalizedLabel, inLocalizedLabel]);

    // Convert to css
    const horizontalLineDyn = {
        // Here only set the dynamic width (later merge with other css properties)
        width: lineSizePx + "px",
    };

    // Hide if widget disabled
    if (!showGeneralInfo) return <></>

    return (
        <Container sx={{ ...stylesSX.container }}>

            <Container sx={stylesSX.rulerContainer}>
                {/* Left ending line */}
                <Box component={"div"} sx={{ ...stylesSX.lineEndings, ...stylesSX.lineEndingLeft }}></Box>
                {/* Horizontal measurement line */}
                <Box component={"div"} sx={{ ...stylesSX.horizontalLine, ...horizontalLineDyn }}></Box>
                {/* Right ending line */}
                <Box component={"div"} sx={{ ...stylesSX.lineEndings, ...stylesSX.lineEndingRight }}></Box>
            </Container>

            <Container sx={stylesSX.textContainer}>
                <Typography sx={stylesSX.textTypo}>
                    {measurementText}
                </Typography>
            </Container>

        </Container>
    )
};

export default ScaleWidget;
